import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { ActivityBookingDetails } from "../model/ActivityBookingDetails";
import { createParticipantsExportSession, ExportSummary } from "../model/participants-export-client";

export default function ExportParticipantsButton({ disabled, getRows }: {
    disabled: boolean;
    getRows: () => ActivityBookingDetails[];
}) {
    const [pending, setPending] = useState<ExportSummary | null>(null);
    const [open, setOpen] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pdfSelected, setPdfSelected] = useState(false);
    const selectedFormat = pdfSelected ? "PDF" : "CSV";
    const busyRef = useRef(false);
    const sessionRef = useRef<ReturnType<typeof createParticipantsExportSession> | null>(null);
    useEffect(() => () => {
        const session = sessionRef.current;
        sessionRef.current = null;
        session?.dispose();
    }, []);
    const close = () => {
        const session = sessionRef.current;
        sessionRef.current = null;
        session?.dispose();
        busyRef.current = false;
        setOpen(false);
        setPreparing(false);
        setBusy(false);
        setPending(null);
    };
    const prepare = async () => {
        if (busyRef.current || sessionRef.current) return;
        busyRef.current = true;
        setOpen(true);
        setPreparing(true);
        setPdfSelected(false);
        let session: ReturnType<typeof createParticipantsExportSession> | null = null;
        try {
            session = createParticipantsExportSession();
            sessionRef.current = session;
            // Paint the dialog before collecting the grid snapshot.
            await new Promise<void>(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
            if (sessionRef.current !== session) return;
            const result = await session.prepare(getRows());
            if (sessionRef.current !== session) return;
            if (!result.bookingCount) {
                toast.info("Nessuna prenotazione da estrarre con i filtri correnti. Le prenotazioni cancellate sono escluse.");
                close();
                return;
            }
            setPending(result);
        } catch {
            if (sessionRef.current !== session) return;
            toast.error("Impossibile preparare l’estrazione. Verifica i dati delle prenotazioni.");
            close();
        } finally {
            if (sessionRef.current === session) {
                busyRef.current = false;
                setPreparing(false);
            }
        }
    };
    const download = async () => {
        const session = sessionRef.current;
        if (!pending || !session || busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        let url: string | undefined;
        const link = document.createElement("a");
        try {
            const blob = await session.download(selectedFormat);
            if (sessionRef.current !== session) return;
            url = URL.createObjectURL(blob);
            link.href = url;
            link.download = `partecipanti-tour-${format(new Date(), "yyyy-MM-dd-HHmmss")}.${selectedFormat.toLowerCase()}`;
            document.body.appendChild(link);
            link.click();
            toast.success(`Estrazione riuscita: ${pending.bookingCount} prenotazioni, ${pending.namedParticipantCount} nominativi presenti, ${pending.missingParticipants} prenotazioni con nominativi mancanti. Download ${selectedFormat} avviato.`);
        } catch {
            if (sessionRef.current === session) toast.error("Estrazione non riuscita. Riprova.");
        } finally {
            if (url) {
                const downloadUrl = url;
                setTimeout(() => { link.remove(); URL.revokeObjectURL(downloadUrl); }, 60000);
            } else {
                link.remove();
            }
            if (sessionRef.current === session) close();
        }
    };
    return <>
        <Button variant="contained" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />} disabled={disabled || open} aria-busy={busy || preparing} onClick={prepare}>{busy ? "Preparazione…" : "Estrai"}</Button>
        <Dialog open={open} onClose={close} transitionDuration={0} aria-labelledby="export-participants-title">
            <DialogTitle id="export-participants-title">Conferma estrazione partecipanti</DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>Il file contiene dati personali. Conservalo in modo sicuro e condividilo solo con persone autorizzate.</Alert>
                <FormControl disabled={busy} sx={{ mb: 2 }}>
                    <FormLabel id="export-format-label">Formato di estrazione</FormLabel>
                    <RadioGroup row aria-labelledby="export-format-label" name="export-format" value={selectedFormat} onChange={(_, value) => setPdfSelected(value === "PDF")}>
                        <FormControlLabel value="CSV" control={<Radio />} label="Excel (CSV)" />
                        <FormControlLabel value="PDF" control={<Radio />} label="PDF per la stampa" />
                    </RadioGroup>
                </FormControl>
                {preparing && <DialogContentText role="status"><CircularProgress size={16} sx={{ mr: 1 }} />Preparazione riepilogo… Puoi già scegliere il formato.</DialogContentText>}
                {pending && <DialogContentText>Estrarre {pending.bookingCount} prenotazioni e {pending.participantCount} partecipanti in {selectedFormat}, raggruppati per giorno e tipo TK? Sono incluse le prenotazioni che rispettano i filtri correnti della griglia, escluse quelle cancellate (CANCELLED).</DialogContentText>}
                <DialogContentText sx={{ mt: 1, minHeight: 40 }}>{pdfSelected ? "PDF in formato A4, con pagine numerate ed elenco ordinato per data, ora, codice prenotazione e nominativo." : "File CSV compatibile con Excel, per consultare ed elaborare i dati."}</DialogContentText>
                {!!pending?.missingParticipants && <Alert severity="warning" sx={{ mt: 2 }}>{pending.missingParticipants} prenotazioni con nominativi mancanti saranno incluse e segnalate nel {selectedFormat}.</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={close}>Annulla</Button>
                <Button variant="contained" disabled={busy || preparing || !pending} startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined} onClick={download}>{busy ? "Preparazione…" : "Conferma estrazione"}</Button>
            </DialogActions>
        </Dialog>
    </>;
}
